import { isNumber, last, round, sortBy, sum, sumBy } from 'lodash-es';
import { DAYS_PER_YEAR, MONTHS_PER_YEAR } from '~/constants/date';
import { date, isDateBetween, isPastDate } from '~/lib/date';
import { safeSum } from '~/lib/jsTools/number';
import { BaseEntity } from '~/lib/model/base';
import { calculateDailyEnergy, calculateDailyPriceByFeePeriod, IAction } from '~/models/common';
import { User } from '~/models/user';
import { Company, ICompanyOutlineResponse } from './company';
import { ICouncilOutlineResponse } from './council';
import { FeePeriod, IRetailer, IRetailerOutlineResponse } from './retailer';

export enum EnergyType {
  ELECTRICITY = 'ELECTRICITY',
  GAS = 'GAS'
}

export enum MeterType {
  NMI = 'NMI',
  MIRN = 'MIRN'
}

export enum MeterRefType {
  Company = 'Company',
  Council = 'Council'
}

export enum NMIMeterType {
  // TODO: Basic/MRIM
  BASIC_MRIM = 'BASIC_MRIM',
  COMMS4 = 'COMMS4'
}

export enum MIRNMeterType {
  BASIC = 'BASIC',
  INTERVAL = 'INTERVAL'
}

export enum SourceOfEstConsumption {
  INTERVAL_DATA = 'INTERVAL_DATA',
  INVOICE = 'INVOICE',
  CUSTOMER_ESTIMATE = 'CUSTOMER_ESTIMATE'
}

// The site classification value itself is the display label ("C&I" / "TARIFF"),
// both in the UI and in what the backend stores. Distinct from ContractType,
// which classifies a contract's pricing and keeps machine values (C_I / TARIFF).
export enum SiteClassification {
  'C&I' = 'C&I',
  TARIFF = 'TARIFF'
}

// Distinct from SiteClassification: this classifies a contract (C&I vs tariff
// pricing), not the site itself. The two happen to share values today but must
// not be unified.
export enum ContractType {
  C_I = 'C_I',
  TARIFF = 'TARIFF'
}

export const ContractTypeLabels: Record<ContractType, string> = {
  [ContractType.C_I]: 'C&I',
  [ContractType.TARIFF]: 'TARIFF'
};

export enum ContractCategory {
  SINGLE_SITE = 'SINGLE_SITE',
  MULTISITE = 'MULTISITE'
}

export enum DMAContractAdditionalServiceType {
  BILL_VALIDATION = 'BILL_VALIDATION',
  FINANCIAL_REPORT = 'FINANCIAL_REPORT',
  USAGE = 'USAGE',
  NETWORK_TARIFF_REVIEW = 'NETWORK_TARIFF_REVIEW',
  CO2_EMISSIONS = 'CO2_EMISSIONS',
  TTEG_PORTAL = 'TTEG_PORTAL',
  VALUE_PACKAGE = 'VALUE_PACKAGE'
}

export interface IMeter {
  id: string;
  energyType: EnergyType;
  meterNumber: string;
  meterType: NMIMeterType | MIRNMeterType;
  streetAddress: string;
  suburb: string;
  state: string;
  postCode: string;
  refType?: MeterRefType;
  refId?: string;
  siteClassification: SiteClassification;
  sourceOfEstConsumption?: SourceOfEstConsumption;
  invoiceBillingPeriodFromDate?: string;
  invoiceBillingPeriodToDate?: string;
  invoiceIssueDate?: string;
  networkDistributor?: string;
  networkTariffCode?: string;
  networkTariffDesc?: string;
  alias?: string;
  description?: string;
  checkSum?: number;
}

export interface NetworkChargeStanding {
  item: string;
  usage?: number;
  unit?: FeePeriod;
  unitPrice: number;
}

export interface NetworkChargeEnergy {
  item: string;
  usage?: number;
  unit?: FeePeriod;
  unitPrice: number;
}

export interface NetworkChargeDemand {
  item: string;
  usage?: number;
  unit?: FeePeriod;
  unitPrice: number;
  monthCount: number;
}

export interface IElectricity {
  numOfMeters: number;
  dlf?: number;
  mlf?: number;
  tlf?: number;
  installedSolarCapacity?: number;
  annualEstPeakEnergy?: number;
  annualEstShoulderEnergy?: number;
  annualEstOffPeakEnergy?: number;
  loadFactor?: number;
  powerFactor?: number;
  maxDemand?: number;
  feedInTariff?: string;
  participantCharge?: number | null;
  ancillaryCharge?: number | null;
  networkChargesStanding?: NetworkChargeStanding[];
  networkChargesEnergy?: NetworkChargeEnergy[];
  networkChargesDemand?: NetworkChargeDemand[];
}

export interface INMI extends IMeter, IElectricity {
  meterType: NMIMeterType;
  action: IAction;
}

export type INMICreateRequest = Pick<
  INMI,
  | 'meterNumber'
  | 'meterType'
  | 'streetAddress'
  | 'suburb'
  | 'state'
  | 'postCode'
  | 'sourceOfEstConsumption'
  | 'invoiceBillingPeriodFromDate'
  | 'invoiceBillingPeriodToDate'
  | 'invoiceIssueDate'
  | 'networkDistributor'
  | 'networkTariffCode'
  | 'networkTariffDesc'
  | 'siteClassification'
  | 'description'
  | 'numOfMeters'
  | 'dlf'
  | 'mlf'
  | 'tlf'
  | 'installedSolarCapacity'
  | 'annualEstPeakEnergy'
  | 'annualEstShoulderEnergy'
  | 'annualEstOffPeakEnergy'
  | 'loadFactor'
  | 'powerFactor'
  | 'maxDemand'
  | 'refType'
  | 'refId'
  | 'feedInTariff'
  | 'participantCharge'
  | 'ancillaryCharge'
  | 'networkChargesStanding'
  | 'networkChargesEnergy'
  | 'networkChargesDemand'
>;

export type INMIUpdateRequest = INMICreateRequest;

export interface IGas {
  uafg?: number;
  annualContractQuantity?: number;
  mdq?: number;
  mhq?: number;
  networkMaximumDailyQuantity?: number;
  loadFactor?: number;
  distributionZone?: string;
}

export interface IMIRN extends IMeter, IGas {
  meterType: MIRNMeterType;
  action: IAction;
}

export type IMIRNCreateRequest = Pick<
  IMIRN,
  | 'meterNumber'
  | 'meterType'
  | 'streetAddress'
  | 'suburb'
  | 'state'
  | 'postCode'
  | 'sourceOfEstConsumption'
  | 'invoiceBillingPeriodFromDate'
  | 'invoiceBillingPeriodToDate'
  | 'invoiceIssueDate'
  | 'networkDistributor'
  | 'networkTariffCode'
  | 'networkTariffDesc'
  | 'siteClassification'
  | 'description'
  | 'uafg'
  | 'annualContractQuantity'
  | 'mdq'
  | 'mhq'
  | 'networkMaximumDailyQuantity'
  | 'loadFactor'
  | 'distributionZone'
  | 'refType'
  | 'refId'
>;

export type IMIRNUpdateRequest = IMIRNCreateRequest;

export class NMI extends BaseEntity implements INMI {
  id!: string;
  energyType!: EnergyType;
  meterNumber!: string;
  meterType!: NMIMeterType;
  streetAddress!: string;
  suburb!: string;
  state!: string;
  postCode!: string;
  refType?: MeterRefType;
  refId?: string;
  siteClassification!: SiteClassification;
  sourceOfEstConsumption?: SourceOfEstConsumption;
  invoiceBillingPeriodFromDate?: string;
  invoiceBillingPeriodToDate?: string;
  invoiceIssueDate?: string;
  networkDistributor?: string;
  networkTariffCode?: string;
  networkTariffDesc?: string;
  description?: string;
  numOfMeters!: number;
  dlf?: number;
  mlf?: number;
  tlf?: number;
  installedSolarCapacity?: number;
  annualEstPeakEnergy?: number;
  annualEstShoulderEnergy?: number;
  annualEstOffPeakEnergy?: number;
  loadFactor?: number;
  powerFactor?: number;
  maxDemand?: number;
  checkSum?: number;
  contracts?: NMIContract[];
  dmaContracts?: DMAContract[];
  feedInTariff?: string;
  activateContractNum?: number | null;
  participantCharge?: number | null;
  ancillaryCharge?: number | null;
  networkChargesStanding?: NetworkChargeStanding[];
  networkChargesEnergy?: NetworkChargeEnergy[];
  networkChargesDemand?: NetworkChargeDemand[];
  company?: Company;
  latestContract?: NMIContract;
  action!: IAction;

  static override from<T = NMI>(raw: any): T {
    const parse = (value: any) => (Array.isArray(value) ? value : JSON.parse(value || '[]'));
    return Object.assign(new NMI(), {
      ...raw,
      networkChargesStanding: parse(raw.networkChargesStanding),
      networkChargesEnergy: parse(raw.networkChargesEnergy),
      networkChargesDemand: parse(raw.networkChargesDemand),
      contracts: raw.contracts?.map((contract: any) => NMIContract.from(contract)),
      dmaContracts: raw.dmaContracts?.map((contract: any) => DMAContract.from(contract)),
      company: Company.from(raw.company),
      annualEstPeakEnergy: raw.annualEstPeakEnergy ?? 0,
      annualEstShoulderEnergy: raw.annualEstShoulderEnergy ?? 0,
      annualEstOffPeakEnergy: raw.annualEstOffPeakEnergy ?? 0
    });
  }

  get annualEstTotalEnergy(): number {
    return sum([this.annualEstOffPeakEnergy, this.annualEstPeakEnergy, this.annualEstShoulderEnergy]);
  }

  get dailyEstPeakEnergy(): number {
    return calculateDailyEnergy(this.annualEstPeakEnergy);
  }

  get dailyEstOffPeakEnergy(): number {
    return calculateDailyEnergy(this.annualEstOffPeakEnergy);
  }

  get dailyEstShoulderEnergy(): number {
    return calculateDailyEnergy(this.annualEstShoulderEnergy);
  }

  get dailyEstTotalEnergy(): number {
    return sum([this.dailyEstPeakEnergy, this.dailyEstOffPeakEnergy, this.dailyEstShoulderEnergy]);
  }

  get activeContract(): NMIContract | undefined {
    return this.contracts?.find((contract) =>
      isDateBetween(date(), contract.contractStartDate, getNMIContractEffectiveEndDate(contract))
    );
  }

  get activeDMAContract(): DMAContract | undefined {
    return this.dmaContracts?.find((contract) =>
      isDateBetween(date(), contract.contractStartDate, contract.contractEndDate)
    );
  }

  get activeContracts(): NMIContract[] {
    return (
      this.contracts?.filter((contract) =>
        isDateBetween(date(), contract.contractStartDate, getNMIContractEffectiveEndDate(contract))
      ) ?? []
    );
  }

  calculateTotalEnergy(): number {
    const { annualEstOffPeakEnergy, annualEstShoulderEnergy, annualEstPeakEnergy } = this;
    return safeSum(annualEstOffPeakEnergy, annualEstShoulderEnergy, annualEstPeakEnergy);
  }

  calculateLoadFactor(): number {
    const { annualEstOffPeakEnergy, annualEstShoulderEnergy, annualEstPeakEnergy } = this;
    if (!isNumber(annualEstPeakEnergy)) {
      return 0;
    }
    return round(
      safeSum(annualEstOffPeakEnergy, annualEstShoulderEnergy, annualEstPeakEnergy) /
        (annualEstPeakEnergy * (DAYS_PER_YEAR / MONTHS_PER_YEAR) * 24),
      2
    );
  }

  calculateAnnualNetworkStanding(): number {
    return sumBy(this.networkChargesStanding, (charge) => {
      const unitPrice = charge.unitPrice ?? 0;
      if (charge.unit === FeePeriod.MONTHLY) {
        return unitPrice * MONTHS_PER_YEAR;
      }
      if (charge.unit === FeePeriod.DAILY) {
        return unitPrice * DAYS_PER_YEAR;
      }
      return unitPrice;
    });
  }

  calculateAnnualNetworkChargesEnergy(totalEnergy?: number): number {
    return sumBy(this.networkChargesEnergy, (charge) => {
      return (
        ((charge.unitPrice / 100) * (charge.usage ?? 1) * (totalEnergy || this.annualEstTotalEnergy)) /
        sumBy(this.networkChargesEnergy, 'usage')
      );
    });
  }

  calculateAnnualNetworkChargesDemand(): number {
    return sumBy(this.networkChargesDemand, (charge) => {
      const unitPrice = charge.unitPrice ?? 0;
      return unitPrice * (charge.usage ?? 1) * charge.monthCount;
    });
  }

  calculateAnnualMarketCharge(): number {
    return (
      (sum([this.ancillaryCharge, this.participantCharge]) * this.annualEstTotalEnergy * (this.dlf || this.tlf || 1)) /
      100
    );
  }
}

export class MIRN extends BaseEntity implements IMIRN {
  id!: string;
  energyType!: EnergyType;
  meterNumber!: string;
  meterType!: MIRNMeterType;
  streetAddress!: string;
  suburb!: string;
  state!: string;
  postCode!: string;
  siteClassification!: SiteClassification;
  sourceOfEstConsumption?: SourceOfEstConsumption;
  invoiceBillingPeriodFromDate?: string;
  invoiceBillingPeriodToDate?: string;
  invoiceIssueDate?: string;
  networkDistributor?: string;
  networkTariffCode?: string;
  networkTariffDesc?: string;
  description?: string;
  uafg?: number;
  totalEnergy?: number;
  mdq?: number;
  mhq?: number;
  networkMaximumDailyQuantity?: number;
  loadFactor?: number;
  distributionZone?: string;
  checkSum?: number;
  contracts?: MIRNContract[];
  annualContractQuantity?: number;
  activateContractNum?: number | null;
  company?: Company;
  latestContract?: MIRNContract;
  action!: IAction;

  calculateLoadFactor(): number {
    const { annualContractQuantity, mdq } = this;
    if (!isNumber(annualContractQuantity) || !isNumber(mdq)) {
      return 0;
    }
    return annualContractQuantity / 12 / (mdq * (365 / 12));
  }

  get activeContract(): IMIRNContract | undefined {
    return this.contracts?.find((contract) =>
      isDateBetween(date(), contract.contractStartDate, contract.contractEndDate)
    );
  }

  get activeContracts(): IMIRNContract[] {
    return (
      this.contracts?.filter((contract) =>
        isDateBetween(date(), contract.contractStartDate, contract.contractEndDate)
      ) ?? []
    );
  }

  get contractsSortedByEndDate(): IMIRNContract[] {
    return (this.contracts ?? []).sort((a, b) => date(b.contractEndDate).diff(date(a.contractEndDate)));
  }
}

export type Meter = NMI | MIRN;

// ============================================================================
// NMI Contract Types (nested REST API)
// ============================================================================

export type GreenPricingType = 'CAL_CERT' | 'CAL_RATE' | 'MAT_CERT' | 'MAT_RATE';

export const isCertPrice = (type?: GreenPricingType): boolean => {
  return type === 'CAL_CERT' || type === 'MAT_CERT';
};

export interface IRatePeriod {
  id?: string;
  startDate: string;
  endDate: string;
  peakRate?: number;
  shoulderRate?: number;
  offPeakRate?: number;
  commodityRate?: number;
  fixedTuosCharge?: number;
  description?: string;
}

export interface IEnvironmentalRatePeriod {
  id?: string;
  startDate: string;
  endDate: string;
  lretType?: GreenPricingType;
  lret?: number;
  sresType?: GreenPricingType;
  sres?: number;
  essType?: GreenPricingType;
  ess?: number;
  veetType?: GreenPricingType;
  veet?: number;
  aeeisType?: GreenPricingType;
  aeeis?: number;
  pdrsType?: GreenPricingType;
  pdrs?: number;
  repsType?: GreenPricingType;
  reps?: number;
  description?: string;
}

export interface INMIContract {
  id: string;
  meterId: string;
  action?: IAction;
  contractKey: 'NMI' | 'MIRN' | 'DMA';
  retailerId: string;
  retailerContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  terminatedDate?: string;
  contractSource?: string;
  contractStatus?: string;
  contractType?: string;
  contractCategory?: string;
  retailerSupplyCharge?: number;
  bundleDemandCharge?: number;
  retailerFixedTariffDiscount?: number;
  retailerTariffDescription?: string;
  specialConditions?: string;
  commissionOnUsage?: number;
  commissionOnMetering?: number;
  estAnnualPeakUsage?: number;
  estAnnualShoulderUsage?: number;
  estAnnualOffPeakUsage?: number;
  estAnnualTotalUsage?: number;
  description?: string;
  solarArrangementDescription?: string;
  greenPowerArrangementDescription?: string;
  meteringCharge?: number;
  meteringServiceFee?: number;
  ratePeriods: IRatePeriod[];
  environmentalRatePeriods: IEnvironmentalRatePeriod[];
}

interface IContractEffectiveEndDate {
  contractEndDate?: string | null;
  terminatedDate?: string | null;
}

export const getNMIContractEffectiveEndDate = <T extends IContractEffectiveEndDate>(
  contract?: T | null
): string | undefined => contract?.terminatedDate || contract?.contractEndDate || undefined;

export const getMeterContractEffectiveEndDate = <T extends IContractEffectiveEndDate>(
  energyType: EnergyType | undefined,
  contract?: T | null
): string | undefined =>
  energyType === EnergyType.GAS ? contract?.contractEndDate || undefined : getNMIContractEffectiveEndDate(contract);

export interface IIdResponse {
  id: string;
}

export interface INMIContractCreateRequest {
  /** NMI scenario is always 'NMI' (fixed value) */
  contractKey: 'NMI';
  retailerId: string;
  retailerContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  contractSource?: string;
  contractType?: string;
  contractCategory?: string;
  retailerSupplyCharge?: number;
  bundleDemandCharge?: number;
  retailerFixedTariffDiscount?: number;
  retailerTariffDescription?: string;
  specialConditions?: string;
  commissionOnUsage?: number;
  commissionOnMetering?: number;
  estAnnualPeakUsage?: number;
  estAnnualShoulderUsage?: number;
  estAnnualOffPeakUsage?: number;
  estAnnualTotalUsage?: number;
  description?: string;
  solarArrangementDescription?: string;
  greenPowerArrangementDescription?: string;
  meteringCharge?: number;
  meteringServiceFee?: number;
  ratePeriods?: IRatePeriod[];
  environmentalRatePeriods?: IEnvironmentalRatePeriod[];
}

export interface INMIContractUpdateRequest extends Partial<INMIContractCreateRequest> {
  id: string;
}

// ============================================================================
// V1 Contract Period Types (deprecated, kept for MIRN compatibility)
// ============================================================================

export interface IContractPeriodCreateRequest {
  periodFromDate: string;
  periodToDate: string;
  peakRate?: number;
  shoulderRate?: number;
  offPeakRate?: number;
  lretRate?: number;
  srecRate?: number;
  veetRate?: number;
  essRate?: number;
  aeeisRate?: number;
  pdrsRate?: number;
  repsRate?: number;
  commodityRate?: number;
  fixedTuosCharge?: number;
}

export interface ISingleContractPeriodCreateRequest extends IContractPeriodCreateRequest {
  contractId: string;
}

export interface IContractPeriod extends IContractPeriodCreateRequest {
  id: string;
}

export type IContractPeriodUpdateRequest = IContractPeriodCreateRequest;

export interface DMAContractAdditionalService {
  id?: string;
  contractId?: string;
  serviceType: DMAContractAdditionalServiceType;
  serviceFee: number;
  frequencyOnMonth: number;
  contractStartDate: string;
  contractEndDate?: string;
  nextTaskDate?: string;
  assignedEmcId: string;
  assignedEmc: User;
}

export interface IDMAContractCreateRequest {
  meterId: string;
  retailerId: string;
  retailContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  ttegCommissionOnMetering?: number;
  totalFee?: number;
  baseFee?: number;
  numOfMeters?: number;
  activeDate?: string;
  agreementSignDate?: string;
  additionalServices?: DMAContractAdditionalService[];
}

export interface IBaseContract {
  id: string;
  retailContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  contractCategory?: string;
  retailSupplyCharge?: number;
  fixedRetailTariffDiscount?: number;
  retailTariffDesc?: string;
  specialConditions?: string;
  ttegCommissionOnUsage?: number;
  ttegCommissionOnMetering?: number;
  periods?: IContractPeriod[];
}

export interface IDMAContract extends IBaseContract {
  meterId: string;
  retailerId: string;
  retailContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  ttegCommissionOnMetering?: number;
  totalFee?: number;
  baseFee?: number;
  numOfMeters?: number;
  activeDate?: string;
  agreementSignDate?: string;
  additionalServices?: DMAContractAdditionalService[];
}

export class NMIContract extends BaseEntity {
  id!: string;
  retailContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  terminatedDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  contractCategory?: string;
  retailSupplyCharge?: number;
  fixedRetailTariffDiscount?: number;
  retailTariffDesc?: string;
  specialConditions?: string;
  ttegCommissionOnUsage?: number;
  ttegCommissionOnMetering?: number;
  // v1 endpoints return this empty since contracts moved to nmi_contract (#376); use ratePeriods instead
  periods!: ContractPeriod[];
  ratePeriods?: IRatePeriod[];
  environmentalRatePeriods?: IEnvironmentalRatePeriod[];
  contractPeakEnergy?: number;
  contractShoulderEnergy?: number;
  contractOffPeakEnergy?: number;
  solarArrangementDesc?: string;
  accreditedGreenPowerArrangementDesc?: string;
  meteringCharge?: number;
  meteringServiceFeePrice?: number;
  sourceOfDmaContract?: string;
  dmaProviderId?: string;
  dmaStartDate?: string;
  dmaEndDate?: string;
  retailerId?: string;
  retailer?: IRetailer;
  contractStatus?: string;

  static override from<T = NMIContract>(raw: any): T {
    return Object.assign(new NMIContract(), {
      ...raw,
      periods: raw.periods?.map((period: any) => ContractPeriod.from(period)),
      contractStatus: isPastDate(getNMIContractEffectiveEndDate(raw)) ? 'Not Active' : 'Active'
    });
  }

  calculateTotalEnergy(): number {
    const { contractOffPeakEnergy, contractShoulderEnergy, contractPeakEnergy } = this;
    return safeSum(contractOffPeakEnergy, contractShoulderEnergy, contractPeakEnergy);
  }

  get activeContractPeriod(): ContractPeriod | undefined {
    return (
      this.periods?.find((period) => isDateBetween(date(), period.periodFromDate, period.periodToDate)) ??
      NMIContract.toContractPeriod(
        this.ratePeriods?.find((period) => isDateBetween(date(), period.startDate, period.endDate)),
        this.environmentalRatePeriods?.find((period) => isDateBetween(date(), period.startDate, period.endDate))
      )
    );
  }

  // Most recent period when none is currently active, mirroring the old `periods` fallback
  get latestContractPeriod(): ContractPeriod | undefined {
    return NMIContract.toContractPeriod(
      last(sortBy(this.ratePeriods ?? [], 'endDate')),
      last(sortBy(this.environmentalRatePeriods ?? [], 'endDate'))
    );
  }

  // Recombine the split rate periods into the legacy single-period shape (rates + environmental rates)
  private static toContractPeriod(
    ratePeriod?: IRatePeriod,
    environmentalRatePeriod?: IEnvironmentalRatePeriod
  ): ContractPeriod | undefined {
    if (!ratePeriod && !environmentalRatePeriod) {
      return undefined;
    }
    return ContractPeriod.from({
      id: ratePeriod?.id ?? environmentalRatePeriod?.id,
      periodFromDate: ratePeriod?.startDate ?? environmentalRatePeriod?.startDate,
      periodToDate: ratePeriod?.endDate ?? environmentalRatePeriod?.endDate,
      peakRate: ratePeriod?.peakRate,
      shoulderRate: ratePeriod?.shoulderRate,
      offPeakRate: ratePeriod?.offPeakRate,
      lretRate: environmentalRatePeriod?.lret,
      srecRate: environmentalRatePeriod?.sres,
      veetRate: environmentalRatePeriod?.veet,
      essRate: environmentalRatePeriod?.ess,
      aeeisRate: environmentalRatePeriod?.aeeis,
      pdrsRate: environmentalRatePeriod?.pdrs,
      repsRate: environmentalRatePeriod?.reps,
      commodityRate: ratePeriod?.commodityRate,
      fixedTuosCharge: ratePeriod?.fixedTuosCharge
    });
  }

  get dailyMeteringCharge(): number {
    return calculateDailyPriceByFeePeriod(this.meteringCharge);
  }

  get dailyMeteringServiceFee(): number {
    return calculateDailyPriceByFeePeriod(this.meteringServiceFeePrice);
  }

  get isDmaContractActive(): boolean {
    return isDateBetween(date(), this.dmaStartDate, this.dmaEndDate);
  }

  get currentDailyMeteringFee(): number {
    return this.isDmaContractActive ? this.dailyMeteringCharge : 0;
  }

  get currentDailyMeteringServiceFee(): number {
    return this.isDmaContractActive ? this.dailyMeteringServiceFee : 0;
  }
}

export class DMAContract extends BaseEntity implements IDMAContract {
  id!: string;
  meterId!: string;
  retailerId!: string;
  retailer!: IRetailer;
  retailContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  ttegCommissionOnMetering?: number;
  totalFee?: number;
  baseFee?: number;
  numOfMeters?: number;
  activeDate?: string;
  agreementSignDate?: string;
  additionalServices?: DMAContractAdditionalService[];
  contractStatus?: string;

  static override from<T = DMAContract>(raw: any): T {
    return Object.assign(new DMAContract(), {
      ...raw,
      additionalServices: raw?.additionalServices?.map((service: any) => DMAContractAdditionalService.from(service)),
      contractStatus: isPastDate(raw?.contractEndDate) ? 'Not Active' : 'Active',
      ttegCommissionOnMetering:
        raw?.ttegCommissionOnMetering ??
        (raw?.totalFee ?? 0) / (raw?.numOfMeters ?? 0) - (raw?.baseFee ?? 0) * raw?.contractPeriodInYear
    });
  }

  get contractPeriodInYear(): number {
    return date(this.contractEndDate).diff(this.contractStartDate, 'day') / DAYS_PER_YEAR;
  }
}

export interface IMIRNContractCreateRequest {
  meterId: string;
  retailerId: string;
  retailContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  contractCategory?: string;
  retailSupplyCharge?: number;
  fixedRetailTariffDiscount?: number;
  retailTariffDesc?: string;
  specialConditions?: string;
  ttegCommissionOnUsage?: number;
  ttegCommissionOnMetering?: number;
  createContractPeriodRequests?: IContractPeriodCreateRequest[];
  contractAcq?: number;
  contractMdq?: number;
  contractMdqOverrunRate?: number;
  contractTakeOrPay?: number;
}

export interface IMIRNContract extends IBaseContract {
  contractAcq?: number;
  contractMdq?: number;
  contractMdqOverrunRate?: number;
  contractTakeOrPay?: number;
}

export class MIRNContract extends BaseEntity implements IMIRNContract {
  id!: string;
  retailContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  contractCategory?: string;
  retailSupplyCharge?: number;
  fixedRetailTariffDiscount?: number;
  retailTariffDesc?: string;
  specialConditions?: string;
  ttegCommissionOnUsage?: number;
  ttegCommissionOnMetering?: number;
  periods!: ContractPeriod[];
  contractAcq?: number;
  contractMdq?: number;
  contractMdqOverrunRate?: number;
  contractTakeOrPay?: number;
  retailerId?: string;
  retailer?: IRetailer;
  contractStatus?: string;

  static override from<T = MIRNContract>(raw: any): T {
    return Object.assign(new MIRNContract(), {
      ...raw,
      periods: raw.periods?.map((period: any) => ContractPeriod.from(period)),
      contractStatus: isPastDate(raw.contractEndDate) ? 'Not Active' : 'Active'
    });
  }

  get activeContractPeriod(): ContractPeriod | undefined {
    return this.periods?.find((period) => isDateBetween(date(), period.periodFromDate, period.periodToDate));
  }
}

export interface IMIRNContractUpdateRequest {
  retailContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  contractCategory?: string;
  retailSupplyCharge?: number;
  fixedRetailTariffDiscount?: number;
  retailTariffDesc?: string;
  specialConditions?: string;
  ttegCommissionOnUsage?: number;
  ttegCommissionOnMetering?: number;
  contractAcq?: number;
  contractMdq?: number;
  contractMdqOverrunRate?: number;
  contractTakeOrPay?: number;
}

export interface IDMAContractUpdateRequest {
  retailerId: string;
  retailContractRefNumber?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  ttegCommissionOnMetering?: number;
  totalFee?: number;
  baseFee?: number;
  numOfMeters?: number;
  activeDate?: string;
  agreementSignDate?: string;
  additionalServices?: DMAContractAdditionalService[];
}

export enum MeterSearchCriteriaSortBy {
  CREATED_DATE = 'CREATED_DATE',
  UPDATED_DATE = 'UPDATED_DATE',
  METER_NUMBER = 'METER_NUMBER',
  METER_TYPE = 'METER_TYPE',
  ADDRESS_STATE = 'ADDRESS_STATE',
  COMPANY_NAME = 'COMPANY_NAME',
  COMPANY_STATUS = 'COMPANY_STATUS',
  ACCOUNT_MANAGER = 'ACCOUNT_MANAGER',
  LAST_CALL_DATE = 'LAST_CALL_DATE',
  FUTURE_CALL_BACK_DATE = 'FUTURE_CALL_BACK_DATE'
}

export enum MeterSearchCriteriaOrder {
  ASC = 'ASC',
  DESC = 'DESC'
}

export interface MeterSearchCriteria {
  retailerName?: string;
  meterNumber?: number;
  states?: string[];
  contactName?: string;
  contractEndDateTo?: string | null;
  contractEndDateFrom?: string | null;
  contractTypes?: string[];
  usageFloor?: number;
  usageUpper?: number;
  accountManagerIds?: string[];
  sourceOfDmaContract?: string;
  dmaProviderIds?: string[];
  dmaContractEndDateFrom?: string | null;
  dmaContractEndDateTo?: string | null;
  hasDmaContract?: number | boolean | null;
  companyIds?: string[];
  sortBy?: MeterSearchCriteriaSortBy;
  order?: MeterSearchCriteriaOrder;
}

export interface MeterSortConfig {
  sortBy?: MeterSearchCriteriaSortBy;
  order?: MeterSearchCriteriaOrder;
}

export interface IElectricityContractOutlineResponse {
  id: string;
  nmiId?: string;
  nmiNumber?: string;
  retailer?: IRetailerOutlineResponse;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  contractCategory?: string;
  bundleDemandCharge?: number;
  fixedRetailTariffDiscount?: number;
  retailTariffDesc?: string;
  specialConditions?: string;
  ratePeriods?: IRatePeriod[];
  environmentalRatePeriods?: IEnvironmentalRatePeriod[];
  usage?: number;
  dmaStartDate?: string;
  dmaEndDate?: string;
  contractPeakEnergy?: number;
  contractShoulderEnergy?: number;
  contractOffPeakEnergy?: number;
}

export interface INmiDetailPageResponse {
  id: string;
  energyType: EnergyType;
  meterNumber: string;
  meterType: NMIMeterType;
  refType?: string;
  refId?: string;
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postCode?: string;
  sourceOfEstConsumption?: string;
  invoiceBillingPeriodFromDate?: string;
  invoiceBillingPeriodToDate?: string;
  invoiceIssueDate?: string;
  networkDistributor?: string;
  networkTariffCode?: string;
  networkTariffDesc?: string;
  alias?: string;
  siteClassification?: string;
  description?: string;
  checkSum?: number;
  contracts?: IElectricityContractOutlineResponse[];
  dmaContracts?: DMAContract[];
  numOfMeters: number;
  dlf?: number;
  mlf?: number;
  tlf?: number;
  installedSolarCapacity?: number;
  annualEstPeakEnergy?: number;
  annualEstShoulderEnergy?: number;
  annualEstOffPeakEnergy?: number;
  loadFactor?: number;
  powerFactor?: number;
  maxDemand?: number;
  feedInTariff?: string;
  company?: ICompanyOutlineResponse;
  council?: ICouncilOutlineResponse;
  createAt?: string;
  updateAt?: string;
  deleteAt?: string;
  participantCharge?: number | null;
  ancillaryCharge?: number | null;
  networkChargesStanding?: NetworkChargeStanding[];
  networkChargesEnergy?: NetworkChargeEnergy[];
  networkChargesDemand?: NetworkChargeDemand[];
  action: IAction;
}

export interface IGasContractOutlineResponse {
  id: string;
  mirnId?: string;
  mirnNumber?: string;
  retailer?: IRetailerOutlineResponse;
  contractStartDate?: string;
  contractEndDate?: string;
  sourceOfContract?: string;
  contractType?: string;
  contractCategory?: string;
  retailTariffDesc?: string;
  specialConditions?: string;
  usage?: number;
}

export interface IMirnDetailPageResponse {
  id: string;
  energyType: EnergyType;
  meterNumber: string;
  meterType: MIRNMeterType;
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postCode?: string;
  sourceOfEstConsumption?: SourceOfEstConsumption;
  invoiceBillingPeriodFromDate?: string;
  invoiceBillingPeriodToDate?: string;
  invoiceIssueDate?: string;
  networkDistributor?: string;
  networkTariffCode?: string;
  networkTariffDesc?: string;
  alias?: string;
  siteClassification?: SiteClassification;
  description?: string;
  contracts?: IGasContractOutlineResponse[];
  uafg?: number;
  annualContractQuantity?: number;
  mdq?: number;
  mhq?: number;
  networkMaximumDailyQuantity?: number;
  loadFactor?: number;
  distributionZone?: string;
  company?: ICompanyOutlineResponse;
  council?: ICouncilOutlineResponse;
  createAt?: string;
  updateAt?: string;
  deleteAt?: string;
  action: IAction;
}

export class ContractPeriod extends BaseEntity implements IContractPeriod {
  id!: string;
  periodFromDate!: string;
  periodToDate!: string;
  peakRate?: number;
  shoulderRate?: number;
  offPeakRate?: number;
  lretRate?: number;
  srecRate?: number;
  veetRate?: number;
  essRate?: number;
  aeeisRate?: number;
  pdrsRate?: number;
  repsRate?: number;
  commodityRate?: number;
  fixedTuosCharge?: number;

  static override from<T = ContractPeriod>(raw: any): T {
    return Object.assign(new ContractPeriod(), {
      ...raw,
      peakRate: raw.peakRate ?? 0,
      shoulderRate: raw.shoulderRate ?? 0,
      offPeakRate: raw.offPeakRate ?? 0
    });
  }
}

export class DMAContractAdditionalService extends BaseEntity {
  serviceType!: DMAContractAdditionalServiceType;
  serviceFee!: number;
  frequencyOnMonth!: number;
  contractStartDate!: string;
  contractEndDate?: string;
  nextTaskDate?: string;
  assignedEmcId!: string;
  assignedEmc!: User;
}
